import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { arEG } from '@mui/material/locale';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PermissionProvider } from './context/PermissionContext';
import PermissionProtectedRoute from './components/auth/PermissionProtectedRoute';
import BranchSelectionWrapper from './components/auth/BranchSelectionWrapper';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Login from './components/auth/Login';
import DashboardLayout from './components/dashboard/DashboardLayout';
import InvoiceDashboardLayout from './components/dashboard/InvoiceDashboardLayout';
import Dashboard from './components/dashboard/Dashboard';
import NewDashboard from './components/dashboard/NewDashboard';
import WidgetSettingsPage from './components/dashboard/WidgetSettingsPage';
import AdminUserWidgetsPage from './components/dashboard/AdminUserWidgetsPage';
import ContactList from './components/contacts/ContactList';
import CustomerList from './components/contacts/CustomerList';
import AllCustomersLoyaltyPoints from './components/contacts/AllCustomersLoyaltyPoints';
import MarketingRoutes from './components/marketing/MarketingRoutes';
import WarehouseRoutes from './components/warehouses/WarehouseRoutes';
import SupplierList from './components/contacts/SupplierList';
import ContactForm from './components/contacts/ContactForm';
import ContactDetails from './components/contacts/ContactDetails';
import ContactImport from './components/contacts/ContactImport';
import BranchList from './components/settings/BranchList';
import BranchForm from './components/settings/BranchForm';
import TaxList from './components/settings/TaxList';
import TaxForm from './components/settings/TaxForm';
import LinkedAccountSettings from './components/settings/LinkedAccountSettings';
import ZatcaCertificateSettings from './components/settings/ZatcaCertificateSettings';
import DocumentAlertsPage from './pages/DocumentAlertsPage';
import LoyaltyPointsPage from './pages/LoyaltyPointsPage';
import AdvancedSettingsPage from './pages/AdvancedSettingsPage';
import ProductList from './components/products/ProductList';
import ProductForm from './components/products/ProductForm';
import WarehouseTransferRequests from './pages/WarehouseTransferRequests';
import NewWarehouseTransferRequest from './pages/NewWarehouseTransferRequest';
import EditWarehouseTransferRequest from './pages/EditWarehouseTransferRequest';
import ProductDetails from './components/products/ProductDetails';
import ProductLayout from './components/products/ProductLayout';
import ProductProperties from './components/products/ProductProperties';
import UnitList from './components/products/UnitList';
import CategoryList from './components/products/CategoryList';
import ProductImport from './components/products/ProductImport';
import ProductBulkUpdate from './components/products/ProductBulkUpdate';
import ReorderQuantitiesPage from './components/products/ReorderQuantitiesPage';
import CategoryProducts from './components/products/CategoryProducts';
import RelatedProductsPage from './pages/RelatedProductsPage';
import InventoryDisplayPage from './pages/InventoryDisplayPage';
import WooCommerceSettings from './components/woocommerce/WooCommerceSettings';
import WooCommerceOrders from './components/woocommerce/WooCommerceOrders';
import WarehouseLayout from './components/warehouses/WarehouseLayout';
import WarehouseList from './components/warehouses/WarehouseList';
import WarehouseForm from './components/warehouses/WarehouseForm';
import WarehouseTransfer from './components/warehouses/WarehouseTransfer';
import WarehouseTransferList from './components/warehouses/WarehouseTransferList';
import UserList from './components/users/UserList';
import UserForm from './components/users/UserForm';
import UserDetails from './components/users/UserDetails';
import FinanceLayout from './components/finance/FinanceLayout';
import AccountsList from './components/finance/accounts/AccountsList';
import InactiveAccountsList from './components/finance/accounts/InactiveAccountsList';
import AccountForm from 'components/finance/accounts/AccountForm';
import AccountTransactions from './components/finance/accounts/AccountTransactions';
import AccountStatements from './components/finance/accounts/AccountStatements';
import PaymentMethodsList from './components/finance/accounts/PaymentMethodsList';
import PaymentMethodForm from './components/finance/accounts/PaymentMethodForm';
import SpecializedVouchersList from './components/finance/specializedVouchers/SpecializedVouchersList';
import SpecializedVoucherForm from './components/finance/specializedVouchers/SpecializedVoucherForm';
import SpecializedVoucherTemplateForm from './components/finance/specializedVouchers/SpecializedVoucherTemplateForm';
import SpecializedVoucherTemplateSelector from './components/finance/specializedVouchers/SpecializedVoucherTemplateSelector';
import CompactBalanceVoucher from './components/finance/specializedVouchers/CompactBalanceVoucher';
import AccountsFeeGroupsList from './components/finance/accounts/FeeGroupsList';
import AccountsFeeGroupForm from './components/finance/accounts/FeeGroupForm';
import FeeGroupsList from './components/finance/feeGroups/FeeGroupsList';
import FeeGroupForm from './components/finance/feeGroups/FeeGroupForm';
import GeneralLedger from './components/finance/ledger/GeneralLedger';
import JournalEntryForm from 'components/finance/ledger/JournalEntryForm';
import JournalEntryView from './components/finance/ledger/JournalEntryView';
import ReceiptsList from './components/finance/receipts/ReceiptsList';
import ReceiptForm from 'components/finance/receipts/ReceiptForm';
import PaymentsList from './components/finance/payments/PaymentsList';
import PaymentForm from 'components/finance/payments/PaymentForm';
import FinancialReports from './components/finance/reports/FinancialReports';
import TransactionsList from './components/finance/transactions/TransactionsList';
import ExportImport from 'components/finance/export/ExportImport';
import FinancialPage from './pages/FinancialPage';
import InvoiceSettingsPage from './components/invoices/InvoiceSettingsPage';
import InvoicePage from './components/invoices/InvoicePage';
import InvoiceFormPage from './components/invoices/InvoiceFormPage';
import InvoiceEntriesPage from './components/invoices/InvoiceEntriesPage';
import SalesReportsPage from './components/invoices/SalesReportsPage';
import PrintTemplateSettingsPage from './components/invoices/PrintTemplateSettingsPage';
import PrintTemplateEditorPage from './components/invoices/PrintTemplateEditorPage';
import printTemplateRoutes from './routes/PrintTemplateRoutes';
import InvoiceViewPage from './components/invoices/InvoiceViewPage';
import ProductBarcodePage from './components/invoices/ProductBarcodePage';
// مكونات إدارة الصلاحيات
import UserGroupList from './components/userGroups/UserGroupList';
import UserGroupForm from './components/userGroups/UserGroupForm';
import PermissionManager from './components/permissions/PermissionManager';
// Import Inventory Count Components
import InventoryCountList from './components/inventoryCount/InventoryCountList';
import NewInventoryCountRequest from './components/inventoryCount/NewInventoryCountRequest';
import InventoryCountDetails from './components/inventoryCount/InventoryCountDetails';
import NewInventoryCountEntry from './components/inventoryCount/NewInventoryCountEntry';
import InventoryCountApproval from './components/inventoryCount/InventoryCountApproval';
import InventoryCountEntryDetails from './components/inventoryCount/InventoryCountEntryDetails';
import EditInventoryCountEntry from './components/inventoryCount/EditInventoryCountEntry';
// Import Reports Components
import ReportsLayout from './components/reports/ReportsLayout';
import InventoryReport from './components/reports/inventory/InventoryReport';
import InventoryMovement from './components/reports/inventory-movement/InventoryMovement';
import ProductMovement from './components/reports/product-movement/ProductMovement';
import ProductProfitReport from './components/reports/product-profit/ProductProfitReport';
import ProductPricesReport from './components/reports/product-prices/ProductPricesReport';
import CategoryQuantitiesReport from './components/reports/category-quantities/CategoryQuantitiesReport';
import ReorderQuantitiesReportPage from './components/reports/ReorderQuantitiesReportPage';
import ZatcaInvoiceReportPage from './pages/ZatcaInvoiceReportPage';
import PaymentReceiptVouchersReportPage from './pages/PaymentReceiptVouchersReportPage';
import SpecializedVouchersReportPage from './pages/SpecializedVouchersReportPage';
import CommissionsReport from './components/reports/commissions/CommissionsReport';
// مكونات المنتجات الخدمية
const ServiceProductList = React.lazy(() => import('./components/products/serviceProducts/ServiceProductList'));
const ServiceProductForm = React.lazy(() => import('./components/products/serviceProducts/ServiceProductForm'));
const ServiceProductDetails = React.lazy(() => import('./components/products/serviceProducts/ServiceProductDetails'));
const ServiceProductOperations = React.lazy(() => import('./components/products/serviceProducts/ServiceProductOperations'));
const ServiceProductOperationForm = React.lazy(() => import('./components/products/serviceProducts/ServiceProductOperationForm'));

// Create a custom theme with LTR support
const theme = createTheme({
  direction: 'ltr',
  palette: {
    primary: {
      main: '#1976d2',
      light: '#4791db',
      dark: '#115293',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f8f9fa',
      paper: '#ffffff',
    },
    success: {
      main: '#30c3b0',
      light: '#4fd1c0',
      dark: '#25a899',
    },
    info: {
      main: '#0091ea',
    },
    warning: {
      main: '#ff9800',
    },
    error: {
      main: '#f44336',
    },
    text: {
      primary: '#2d3748',
      secondary: '#718096',
    },
  },
  typography: {
    fontFamily: [
      'Segoe UI',
      'Roboto',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 700,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
    subtitle1: {
      fontWeight: 500,
    },
    button: {
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiTextField: {
      defaultProps: {
        size: 'small',
        fullWidth: true,
      },
    },
    MuiButton: {
      defaultProps: {
        size: 'medium',
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          padding: '8px 16px',
          fontWeight: 500,
        },
        contained: {
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          border: '1px solid rgba(0,0,0,0.05)',
          overflow: 'hidden',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: 'none',
          boxShadow: '0 0 20px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '4px 8px',
          '&.Mui-selected': {
            backgroundColor: 'rgba(25, 118, 210, 0.12)',
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 40,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&:hover': {
            backgroundColor: 'rgba(25, 118, 210, 0.08)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 10px rgba(0,0,0,0.08)',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 3,
          borderRadius: '3px 3px 0 0',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          margin: '0 8px',
        },
      },
    },
  },
}, arEG); // Add Arabic locale

// App content component that has access to auth context
const AppContent = () => {
  // Note: We don't need to destructure showBranchDialog as it's handled in BranchSelectionWrapper
  
  return (
    <>
      {/* Branch selection dialog that can appear anywhere in the app */}
      <BranchSelectionWrapper />
      
      <Router>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          
          {/* Redirect root to dashboard or login */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          
          {/* Protected Routes */}
          <Route path="/dashboard/*" element={
            <ProtectedRoute>
              <DashboardLayout>
                <NewDashboard />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/contacts/*" element={
            <ProtectedRoute>
              <DashboardLayout>
                <Routes>
                  {/* تطبيق حماية الصلاحيات على كل صفحة فرعية */}
                  <Route path="/" element={
                    <PermissionProtectedRoute sectionId="contacts" pageId="all">
                      <ContactList />
                    </PermissionProtectedRoute>
                  } />
                  <Route path="customers" element={
                    <PermissionProtectedRoute sectionId="contacts" pageId="customers" actionId="view_page">
                      <CustomerList />
                    </PermissionProtectedRoute>
                  } />
                  <Route path="suppliers" element={
                    <PermissionProtectedRoute sectionId="contacts" pageId="suppliers" actionId="view_page">
                      <SupplierList />
                    </PermissionProtectedRoute>
                  } />
                  <Route path="import" element={
                    <PermissionProtectedRoute sectionId="contacts" pageId="import">
                      <ContactImport />
                    </PermissionProtectedRoute>
                  } />
                  <Route path="new" element={
                    <PermissionProtectedRoute sectionId="contacts" pageId="all" actionId="create">
                      <ContactForm />
                    </PermissionProtectedRoute>
                  } />
                  <Route path="edit/:id" element={
                    <PermissionProtectedRoute sectionId="contacts" pageId="all" actionId="edit">
                      <ContactForm />
                    </PermissionProtectedRoute>
                  } />
                  <Route path=":id" element={
                    <PermissionProtectedRoute sectionId="contacts" pageId="all" actionId="view">
                      <ContactDetails />
                    </PermissionProtectedRoute>
                  } />
                  <Route path="linked-accounts" element={
                    <PermissionProtectedRoute sectionId="contacts" pageId="linked-accounts">
                      <LinkedAccountSettings />
                    </PermissionProtectedRoute>
                  } />
                  <Route path="loyalty-points" element={
                    <PermissionProtectedRoute sectionId="contacts" pageId="loyalty-points">
                      <LoyaltyPointsPage />
                    </PermissionProtectedRoute>
                  } />
                  <Route path="all-loyalty-points" element={
                    <PermissionProtectedRoute sectionId="contacts" pageId="all-loyalty-points">
                      <AllCustomersLoyaltyPoints />
                    </PermissionProtectedRoute>
                  } />
                </Routes>
              </DashboardLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/settings/*" element={
            <ProtectedRoute>
              <DashboardLayout>
                <Routes>
                  <Route path="branches" element={<BranchList />} />
                  <Route path="branches/new" element={<BranchForm />} />
                  <Route path="branches/edit/:id" element={<BranchForm />} />
                  <Route path="taxes" element={<TaxList />} />
                  <Route path="taxes/new" element={<TaxForm mode="create" />} />
                  <Route path="taxes/edit/:id" element={<TaxForm mode="edit" />} />
                  <Route path="zatca-certificates" element={
                    <PermissionProtectedRoute sectionId="settings" pageId="zatca-certificates" actionId="view_page">
                      <ZatcaCertificateSettings />
                    </PermissionProtectedRoute>
                  } />
                  <Route path="dashboard-widgets" element={<WidgetSettingsPage />} />
                  <Route path="admin/user-widgets" element={<AdminUserWidgetsPage />} />
                  <Route path="document-alerts" element={<DocumentAlertsPage />} />
                  <Route path="advanced" element={<AdvancedSettingsPage />} />
                </Routes>
              </DashboardLayout>
            </ProtectedRoute>
          } />
          
          {/* Products Routes */}
          <Route path="/products/*" element={
            <ProtectedRoute>
              <DashboardLayout>
                <Routes>
                  <Route element={<ProductLayout />}>
                    <Route path="/" element={
                      <PermissionProtectedRoute sectionId="products" pageId="all" actionId="view_page">
                        <ProductList />
                      </PermissionProtectedRoute>
                    } />
                    <Route path="units" element={
                      <PermissionProtectedRoute sectionId="products" pageId="units" actionId="view_page">
                        <UnitList />
                      </PermissionProtectedRoute>
                    } />
                    <Route path="categories" element={
                      <PermissionProtectedRoute sectionId="products" pageId="categories" actionId="view_page">
                        <CategoryList />
                      </PermissionProtectedRoute>
                    } />
                    <Route path="properties" element={
                      <PermissionProtectedRoute sectionId="products" pageId="attributes" actionId="view_page">
                        <ProductProperties />
                      </PermissionProtectedRoute>
                    } />
                    {/* المنتجات الخدمية */}
                    <Route path="service-products" element={
                      <PermissionProtectedRoute sectionId="products" pageId="service-products" actionId="view_page">
                        <Suspense fallback={<div>جاري التحميل...</div>}>
                          <ServiceProductList />
                        </Suspense>
                      </PermissionProtectedRoute>
                    } />
                    <Route path="service-operations/edit/:id" element={
                      <PermissionProtectedRoute sectionId="products" pageId="service-product-operations" actionId="edit">
                        <Suspense fallback={<div>جاري التحميل...</div>}>
                          <ServiceProductOperationForm mode="edit" />
                        </Suspense>
                      </PermissionProtectedRoute>
                    } />
                    <Route path="service-operations/new" element={
                      <PermissionProtectedRoute sectionId="products" pageId="service-product-operations" actionId="create">
                        <Suspense fallback={<div>جاري التحميل...</div>}>
                          <ServiceProductOperationForm />
                        </Suspense>
                      </PermissionProtectedRoute>
                    } />
                    <Route path="service-operations/:id" element={
                      <PermissionProtectedRoute sectionId="products" pageId="service-product-operations" actionId="view">
                        <Suspense fallback={<div>جاري التحميل...</div>}>
                          <ServiceProductOperationForm mode="view" />
                        </Suspense>
                      </PermissionProtectedRoute>
                    } />
                    <Route path="service-operations" element={
                      <PermissionProtectedRoute sectionId="products" pageId="service-product-operations" actionId="view_page">
                        <Suspense fallback={<div>جاري التحميل...</div>}>
                          <ServiceProductOperations />
                        </Suspense>
                      </PermissionProtectedRoute>
                    } />
                    <Route path="service-products/new" element={
                      <PermissionProtectedRoute sectionId="products" pageId="service-products" actionId="create">
                        <Suspense fallback={<div>جاري التحميل...</div>}>
                          <ServiceProductForm />
                        </Suspense>
                      </PermissionProtectedRoute>
                    } />
                    <Route path="new" element={
                      <PermissionProtectedRoute sectionId="products" pageId="add-new" actionId="view_page">
                        <ProductForm />
                      </PermissionProtectedRoute>
                    } />
                    <Route path="import" element={
                      <PermissionProtectedRoute sectionId="products" pageId="import" actionId="view_page">
                        <ProductImport />
                      </PermissionProtectedRoute>
                    } />
                    <Route path="bulk-update" element={
                      <PermissionProtectedRoute sectionId="products" pageId="bulk-update" actionId="view_page">
                        <ProductBulkUpdate />
                      </PermissionProtectedRoute>
                    } />
                    <Route path="reorder-quantities" element={
                      <PermissionProtectedRoute sectionId="products" pageId="reorder-quantities" actionId="view_page">
                        <ReorderQuantitiesPage />
                      </PermissionProtectedRoute>
                    } />
                    <Route path="related-products" element={
                      <PermissionProtectedRoute sectionId="products" pageId="related-products" actionId="view_page">
                        <RelatedProductsPage />
                      </PermissionProtectedRoute>
                    } />
                  </Route>
                  <Route path="service-operations/:id" element={
                    <PermissionProtectedRoute sectionId="products" pageId="service-product-operations" actionId="view">
                      <Suspense fallback={<div>جاري التحميل...</div>}>
                        <ServiceProductOperationForm mode="view" />
                      </Suspense>
                    </PermissionProtectedRoute>
                  } />
                  <Route path="service-operations/edit/:id" element={
                    <PermissionProtectedRoute sectionId="products" pageId="service-product-operations" actionId="edit">
                      <Suspense fallback={<div>جاري التحميل...</div>}>
                        <ServiceProductOperationForm mode="edit" />
                      </Suspense>
                    </PermissionProtectedRoute>
                  } />
                  <Route path="service-products/edit/:id" element={
                    <PermissionProtectedRoute sectionId="products" pageId="service-products" actionId="edit">
                      <Suspense fallback={<div>جاري التحميل...</div>}>
                        <ServiceProductForm />
                      </Suspense>
                    </PermissionProtectedRoute>
                  } />
                  <Route path="service-products/:id" element={
                    <PermissionProtectedRoute sectionId="products" pageId="service-products" actionId="view">
                      <Suspense fallback={<div>جاري التحميل...</div>}>
                        <ServiceProductDetails />
                      </Suspense>
                    </PermissionProtectedRoute>
                  } />
                  <Route path="edit/:id" element={
                    <PermissionProtectedRoute sectionId="products" pageId="all" actionId="edit">
                      <ProductForm />
                    </PermissionProtectedRoute>
                  } />
                  <Route path=":id" element={
                    <PermissionProtectedRoute sectionId="products" pageId="all" actionId="view">
                      <ProductDetails />
                    </PermissionProtectedRoute>
                  } />
                </Routes>
              </DashboardLayout>
            </ProtectedRoute>
          } />
          
          {/* Units Route */}
          <Route path="/units/*" element={
            <ProtectedRoute>
              <DashboardLayout>
                <Routes>
                  <Route path="/" element={
                    <PermissionProtectedRoute sectionId="products" pageId="units" actionId="view_page">
                      <UnitList />
                    </PermissionProtectedRoute>
                  } />
                </Routes>
              </DashboardLayout>
            </ProtectedRoute>
          } />
          
          {/* Categories Route */}
          <Route path="/categories/*" element={
            <ProtectedRoute>
              <DashboardLayout>
                <Routes>
                  <Route path="/" element={
                    <PermissionProtectedRoute sectionId="products" pageId="categories" actionId="view_page">
                      <CategoryList />
                    </PermissionProtectedRoute>
                  } />
                </Routes>
              </DashboardLayout>
            </ProtectedRoute>
          } />
          
          {/* Ruta para productos de categoría */}
          <Route path="/category-products/:categoryId" element={
            <ProtectedRoute>
              <DashboardLayout>
                <CategoryProducts />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          
          {/* Warehouses Routes */}
          <Route path="/warehouses/*" element={
            <ProtectedRoute>
              <DashboardLayout>
                <WarehouseRoutes />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          
          {/* Warehouse Transfer Requests Routes */}
          <Route path="/warehouse-transfers/*" element={
            <ProtectedRoute>
              <DashboardLayout>
                <Routes>
                  <Route path="requests" element={<WarehouseTransferRequests />} />
                  <Route path="requests/new" element={<NewWarehouseTransferRequest />} />
                  <Route path="requests/edit/:id" element={<EditWarehouseTransferRequest />} />
                </Routes>
              </DashboardLayout>
            </ProtectedRoute>
          } />
          
          {/* Inventory Count Routes */}
          <Route path="/inventory-count/*" element={
            <ProtectedRoute>
              <DashboardLayout>
                <Routes>
                  <Route path="/" element={
                    <PermissionProtectedRoute sectionId="inventory-count" pageId="list" actionId="view_page">
                      <InventoryCountList />
                    </PermissionProtectedRoute>
                  } />
                  <Route path="new" element={
                    <PermissionProtectedRoute sectionId="inventory-count" pageId="new-request" actionId="view_page">
                      <NewInventoryCountRequest />
                    </PermissionProtectedRoute>
                  } />
                  <Route path=":id" element={
                    <PermissionProtectedRoute sectionId="inventory-count" pageId="list" actionId="view">
                      <InventoryCountDetails />
                    </PermissionProtectedRoute>
                  } />
                  <Route path=":id/entries/new" element={
                    <PermissionProtectedRoute sectionId="inventory-count" pageId="list" actionId="add_entry">
                      <NewInventoryCountEntry />
                    </PermissionProtectedRoute>
                  } />
                  <Route path=":id/approve" element={
                    <PermissionProtectedRoute sectionId="inventory-count" pageId="list" actionId="approve">
                      <InventoryCountApproval />
                    </PermissionProtectedRoute>
                  } />
                  <Route path="entries/:entryId" element={
                    <PermissionProtectedRoute sectionId="inventory-count" pageId="list" actionId="view_entry">
                      <InventoryCountEntryDetails />
                    </PermissionProtectedRoute>
                  } />
                  <Route path="entries/:entryId/edit" element={
                    <PermissionProtectedRoute sectionId="inventory-count" pageId="list" actionId="edit_entry">
                      <EditInventoryCountEntry />
                    </PermissionProtectedRoute>
                  } />
                </Routes>
              </DashboardLayout>
            </ProtectedRoute>
          } />

          {/* Reports Routes */}
          <Route path="/reports/*" element={
            <ProtectedRoute>
              <DashboardLayout>
                <Routes>
                  <Route element={<ReportsLayout />}>
                    <Route path="/" element={<Navigate to="/reports/inventory" replace />} />
                    <Route path="inventory" element={<InventoryReport />} />
                    <Route path="inventory-display" element={
                      <PermissionProtectedRoute sectionId="reports" pageId="inventory-display-report" actionId="view_page">
                        <InventoryDisplayPage />
                      </PermissionProtectedRoute>
                    } />
                    <Route path="inventory-movement" element={<InventoryMovement />} />
                    <Route path="product-movement" element={<ProductMovement />} />
                    <Route path="product-profit" element={<ProductProfitReport />} />
                    <Route path="product-prices" element={
                      <PermissionProtectedRoute sectionId="reports" pageId="product-prices" actionId="view_page">
                        <ProductPricesReport />
                      </PermissionProtectedRoute>
                    } />
                    <Route path="category-quantities" element={
                      <PermissionProtectedRoute sectionId="reports" pageId="category-quantities-report" actionId="view_page">
                        <CategoryQuantitiesReport />
                      </PermissionProtectedRoute>
                    } />
                    <Route path="reorder-quantities" element={<ReorderQuantitiesReportPage />} />
                    <Route path="zatca-invoices" element={<ZatcaInvoiceReportPage />} />
                    <Route path="payment-receipt-vouchers" element={<PaymentReceiptVouchersReportPage />} />
                    <Route path="specialized-vouchers" element={<SpecializedVouchersReportPage />} />
                    <Route path="commissions" element={
                      <PermissionProtectedRoute sectionId="reports" pageId="sales-commissions" actionId="view_page">
                        <CommissionsReport />
                      </PermissionProtectedRoute>
                    } />
                  </Route>
                </Routes>
              </DashboardLayout>
            </ProtectedRoute>
          } />
          
          {/* Direct Receipts Routes (Without Finance prefix) */}
          <Route path="/receipts/*" element={
            <ProtectedRoute>
              <DashboardLayout>
                <Routes>
                  <Route path="/" element={<ReceiptsList />} />
                  <Route path="new" element={<ReceiptForm />} />
                  <Route path="edit/:id" element={<ReceiptForm />} />
                  <Route path=":id" element={<ReceiptForm />} />
                  <Route path="print/:id" element={<ReceiptForm />} />
                </Routes>
              </DashboardLayout>
            </ProtectedRoute>
          } />

          {/* New Financial Routes with Improved UI */}
          <Route path="/financial/*" element={
            <ProtectedRoute>
              <DashboardLayout>
                <FinancialPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          
          {/* Users Routes */}
          <Route path="/users/*" element={
            <ProtectedRoute>
              <DashboardLayout>
                <Routes>
                  <Route path="/" element={<UserList />} />
                  <Route path="new" element={<UserForm mode="create" />} />
                  <Route path="edit/:id" element={<UserForm mode="edit" />} />
                  <Route path=":id" element={<UserDetails />} />
                </Routes>
              </DashboardLayout>
            </ProtectedRoute>
          } />
          
          {/* مسارات إدارة الصلاحيات */}
          <Route path="/admin/*" element={
            <ProtectedRoute>
              <DashboardLayout>
                <Routes>
                  <Route path="user-groups" element={<UserGroupList />} />
                  <Route path="user-groups/new" element={<UserGroupForm mode="create" />} />
                  <Route path="user-groups/edit/:id" element={<UserGroupForm mode="edit" />} />
                  <Route path="permissions/:groupId" element={<PermissionManager />} />
                  <Route path="*" element={<Navigate to="/admin/user-groups" replace />} />
                </Routes>
              </DashboardLayout>
            </ProtectedRoute>
          } />
          
          {/* Marketing Routes */}
          <Route path="/marketing/*" element={
            <ProtectedRoute>
              <DashboardLayout>
                <MarketingRoutes />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          
          {/* Invoice Routes - Creation/Edit (No Sidebar) */}
          <Route path="/invoices/:pageId/new" element={
            <ProtectedRoute>
              <InvoiceDashboardLayout>
                <InvoiceFormPage />
              </InvoiceDashboardLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/invoices/:pageId/:invoiceId/edit" element={
            <ProtectedRoute>
              <InvoiceDashboardLayout>
                <InvoiceFormPage />
              </InvoiceDashboardLayout>
            </ProtectedRoute>
          } />
          
          {/* Invoice Routes - Standard Layout */}
          <Route path="/invoices" element={
            <ProtectedRoute>
              <DashboardLayout>
                <Navigate to="/invoices/settings" replace />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/invoices/settings" element={
            <ProtectedRoute>
              <DashboardLayout>
                <InvoiceSettingsPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/invoices/entries" element={
            <ProtectedRoute>
              <DashboardLayout>
                <InvoiceEntriesPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />

          {/* WooCommerce Integration Routes */}
          <Route path="/invoices/woocommerce/settings" element={
            <ProtectedRoute>
              <DashboardLayout>
                <WooCommerceSettings />
              </DashboardLayout>
            </ProtectedRoute>
          } />

          <Route path="/invoices/woocommerce/orders" element={
            <ProtectedRoute>
              <DashboardLayout>
                <WooCommerceOrders />
              </DashboardLayout>
            </ProtectedRoute>
          } />

          <Route path="/invoices/:pageId" element={
            <ProtectedRoute>
              <DashboardLayout>
                <InvoicePage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/invoices/:pageId/:invoiceId/view" element={
            <ProtectedRoute>
              <DashboardLayout>
                <InvoiceViewPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/invoices/:pageId/:invoiceId" element={
            <ProtectedRoute>
              <DashboardLayout>
                <InvoicePage />
              </DashboardLayout>
            </ProtectedRoute>
          } />

          {/* ملخص الفواتير */}
          <Route path="/invoices/reports/summary" element={
            <ProtectedRoute>
              <DashboardLayout>
                <SalesReportsPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
         
         {/* قوالب الطباعة */}
         <Route path="/invoices/print-templates" element={
           <ProtectedRoute>
             <DashboardLayout>
               <PrintTemplateSettingsPage />
             </DashboardLayout>
           </ProtectedRoute>
         } />
         {/* طباعة باركود المنتجات - مسار خاص يجب أن يكون قبل المسار الديناميكي /:pageId */}
         <Route path="/invoices/barcode-print" element={
           <ProtectedRoute>
             <DashboardLayout>
               <ProductBarcodePage />
             </DashboardLayout>
           </ProtectedRoute>
         } />
         
         {/* للتوافق مع الروابط القديمة */}
         <Route path="/invoices/product-barcodes" element={
           <ProtectedRoute>
             <DashboardLayout>
               <ProductBarcodePage />
             </DashboardLayout>
           </ProtectedRoute>
         } />
         
         <Route path="/invoices/print-templates/editor/:id" element={
           <ProtectedRoute>
             <DashboardLayout>
               <PrintTemplateEditorPage />
             </DashboardLayout>
           </ProtectedRoute>
         } />
          
          {/* Finance Routes */}
          <Route path="/finance/*" element={
            <ProtectedRoute>
              <DashboardLayout>
                <Routes>
                  <Route path="/" element={<Navigate to="/finance/accounts" replace />} />
                  <Route element={<FinanceLayout />}>
                    {/* إدارة الحسابات العامة */}
                    <Route path="accounts/*" element={
                      <Routes>
                        <Route index element={<AccountsList />} />
                        <Route path="inactive" element={<InactiveAccountsList />} />
                        <Route path="new" element={<AccountForm />} />
                        <Route path="edit/:id" element={<AccountForm />} />
                        <Route path=":accountId/transactions" element={<AccountTransactions />} />
                        
                        {/* طرق الدفع داخل الحسابات العامة */}
                        <Route path="payment-methods" element={<PaymentMethodsList />} />
                        <Route path="payment-methods/new" element={
                          <PaymentMethodForm
                            onSuccess={() => window.history.back()}
                            onCancel={() => window.history.back()}
                          />
                        } />
                        <Route path="payment-methods/edit/:id" element={
                          <PaymentMethodForm
                            onSuccess={() => window.history.back()}
                            onCancel={() => window.history.back()}
                          />
                        } />
                        
                        {/* مجموعات الرسوم داخل الحسابات العامة */}
                        <Route path="fee-groups" element={<AccountsFeeGroupsList />} />
                        <Route path="fee-groups/new" element={<AccountsFeeGroupForm />} />
                        <Route path="fee-groups/edit/:id" element={<AccountsFeeGroupForm />} />
                      </Routes>
                    } />
                    
                    {/* كشف حساب */}
                    <Route path="account-statements" element={<AccountStatements />} />
                    
                    {/* دفتر الأستاذ العام */}
                    <Route path="ledger" element={<GeneralLedger />} />
                    <Route path="ledger/new-entry" element={<JournalEntryForm />} />
                    <Route path="ledger/edit/:id" element={<JournalEntryForm />} />
                    <Route path="ledger/view/:id" element={<JournalEntryView />} />
                    
                    {/* سندات القبض */}
                    <Route path="receipts" element={<ReceiptsList />} />
                    <Route path="receipts/new" element={<ReceiptForm />} />
                    <Route path="receipts/edit/:id" element={<ReceiptForm />} />
                    
                    {/* سندات الصرف */}
                    <Route path="payments" element={<PaymentsList />} />
                    <Route path="payments/new" element={<PaymentForm />} />
                    <Route path="payments/edit/:id" element={<PaymentForm />} />
                    
                    {/* التقارير المالية */}
                    <Route path="reports" element={<FinancialReports />} />
                    
                    {/* إدارة الأرصدة والمعاملات */}
                    <Route path="transactions" element={<TransactionsList />} />
                    
                    
                    {/* السندات المتخصصة */}
                    <Route path="specialized-vouchers" element={<SpecializedVouchersList />} />
                    <Route path="specialized-vouchers/templates/new" element={<SpecializedVoucherTemplateForm />} />
                    <Route path="specialized-vouchers/templates/edit/:id" element={<SpecializedVoucherTemplateForm />} />
                    <Route path="specialized-vouchers/template/:id" element={<SpecializedVouchersList />} />
                    <Route path="specialized-vouchers/new" element={<SpecializedVoucherTemplateSelector />} />
                    <Route path="specialized-vouchers/new/:templateId" element={<SpecializedVoucherForm />} />
                    <Route path="specialized-vouchers/edit/:id" element={<SpecializedVoucherForm />} />
                    <Route path="specialized-vouchers/compact/:id" element={<CompactBalanceVoucher />} />
                    <Route path="specialized-vouchers/:id" element={<SpecializedVoucherForm />} />
                    {/* مجموعات الرسوم - مسارات مباشرة */}
                    <Route path="fee-groups" element={<FeeGroupsList />} />
                    <Route path="fee-groups/create" element={<FeeGroupForm />} />
                    <Route path="fee-groups/edit/:id" element={<FeeGroupForm />} />
                    <Route path="fee-groups/edit/:id" element={<FeeGroupForm />} />
                    
                    {/* التصدير والاستيراد */}
                    <Route path="export-import" element={<ExportImport />} />
                  </Route>
                </Routes>
              </DashboardLayout>
            </ProtectedRoute>
          } />
          
          {/* Catch all - redirect to login */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <PermissionProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <AppContent />
        </ThemeProvider>
      </PermissionProvider>
    </AuthProvider>
  );
}

export default App;